import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Leaf, Sun, Droplets, TrendingUp, Shield } from "lucide-react";
import { useSEO } from "../hooks/useSEO";

const IMG_MISCANTHUS = "/images/Biomasse-energie.jpeg";
const IMG_BANNER = "/images/Champ-4.webp";
const IMG_RHIZOMES = "/images/Champ-2.webp";

const avantages = [
  { icon: Leaf, title: "Culture pérenne", desc: "Plus de 15 ans de production après une seule plantation. Un investissement durable et rentable." },
  { icon: Shield, title: "Aucun pesticide", desc: "Naturellement résistant aux maladies et ravageurs. Culture 100% naturelle, sans intrants chimiques." },
  { icon: Droplets, title: "Peu d'eau", desc: "Système racinaire profond (jusqu'à 3m) permettant une grande résistance à la sécheresse." },
  { icon: Sun, title: "Bilan carbone positif", desc: "Absorbe plus de CO2 qu'il n'en émet. Une culture idéale pour lutter contre le changement climatique." },
  { icon: TrendingUp, title: "Rendement élevé", desc: "10 à 25 tonnes de matière sèche par hectare à maturité (à partir de l'an 4-5)." },
  { icon: ArrowRight, title: "Polyvalent", desc: "Utilisable en biomasse énergie, paillage, litière animale, construction et isolation." },
];

export default function LeMiscanthus() {
  useSEO({
    title: "Le Miscanthus giganteus : avantages et utilisations | Miscanthus Sarthe",
    description: "Culture pérenne (15 ans et plus), sans pesticide, 10 à 25 t/ha : découvrez le Miscanthus giganteus, ses avantages et ses usages en biomasse, paillage et litière.",
    path: "/le-miscanthus",
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <main data-testid="miscanthus-page" style={{ backgroundColor: "#f5f2ec" }}>
      {/* Header */}
      <div className="page-header" style={{ paddingTop: "7rem" }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="section-divider mb-4" />
          <h1 className="font-display text-5xl sm:text-6xl mb-5" style={{ color: "#3a2b24" }}>Le Miscanthus</h1>
          <p className="text-base max-w-2xl" style={{ color: "#3a2b24", opacity: 0.7, fontFamily: "'Satoshi', sans-serif" }}>
            La graminée géante qui révolutionne l'agriculture durable en Sarthe et dans les Pays de la Loire.
          </p>
        </div>
      </div>

      {/* Hero image */}
      <div className="relative h-80 sm:h-96 overflow-hidden">
        <img src={IMG_BANNER} alt="Champ de miscanthus" className="w-full h-full object-cover" style={{ objectPosition: "bottom" }} />
        <div className="absolute inset-0" style={{ background: "linear-gradient(to top, rgba(245,242,236,1) 0%, transparent 60%)" }} />
      </div>

      {/* Description */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
            <div>
              <h2 className="font-display text-3xl sm:text-4xl mb-6" style={{ color: "#3a2b24" }}>
                Qu'est-ce que le Miscanthus ?
              </h2>
              <div className="space-y-4 text-base leading-relaxed" style={{ color: "#3a2b24", opacity: 0.8, fontFamily: "'Satoshi', sans-serif" }}>
                <p>
                  Le <strong>Miscanthus giganteus</strong> est une graminée vivace géante originaire d'Asie du Sud-Est (Chine, Japon). Introduite en Europe dans les années 1990 pour ses propriétés énergétiques exceptionnelles, elle s'est rapidement imposée comme une culture d'avenir pour l'agriculture durable.
                </p>
                <p>
                  Cette plante remarquable peut atteindre <strong>2 à 4 mètres de hauteur</strong> en une seule saison de croissance. Elle se développe à partir de rhizomes (tiges souterraines) et repousse chaque printemps sans besoin de replantation.
                </p>
                <p>
                  En Sarthe et dans les Pays de la Loire, le miscanthus prospère particulièrement bien grâce au climat tempéré de la région. L'EARL Torcé Biomasse Energie cultive cette plante depuis plusieurs années et en a maîtrisé toutes les subtilités culturales.
                </p>
              </div>
            </div>
            <div className="rounded-2xl overflow-hidden">
              <img src={IMG_RHIZOMES} alt="Champ de miscanthus" loading="lazy" decoding="async" className="w-full object-cover" style={{ aspectRatio: "4/3", objectPosition: "center 75%" }} />
            </div>
          </div>
        </div>
      </section>

      {/* Avantages */}
      <section style={{ backgroundColor: "#eae4d8" }} className="py-16 px-4 sm:px-6 lg:px-8" data-testid="avantages-section">
        <div className="max-w-7xl mx-auto">
          <div className="section-divider mb-4" />
          <h2 className="font-display text-3xl sm:text-4xl mb-12" style={{ color: "#3a2b24" }}>Pourquoi choisir le Miscanthus ?</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {avantages.map((a, i) => (
              <div key={i} className="p-6 rounded-xl" style={{ backgroundColor: "white", border: "1px solid #d8d1c4" }}>
                <a.icon size={28} style={{ color: "#2d5016" }} className="mb-4" />
                <h3 className="font-display text-xl mb-2" style={{ color: "#3a2b24" }}>{a.title}</h3>
                <p className="text-sm leading-relaxed" style={{ color: "#3a2b24", opacity: 0.7, fontFamily: "'Satoshi', sans-serif" }}>{a.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Utilisations */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <h2 className="font-display text-3xl sm:text-4xl mb-8" style={{ color: "#3a2b24" }}>Utilisations du Miscanthus</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {[
              { title: "Biomasse énergie", desc: "Le miscanthus est une excellente source de biomasse pour le chauffage. Son pouvoir calorifique est comparable à celui du bois avec un bilan carbone très favorable.", img: IMG_MISCANTHUS },
              { title: "Paillage & litière", desc: "La paille de miscanthus est idéale pour le paillage de jardins, potagers et cultures. Sa grande absorbance en fait aussi une litière de premier choix pour chevaux, bovins et volailles.", img: "/images/Paille-vrac.jpeg" },
            ].map((u, i) => (
              <div key={i} className="rounded-xl overflow-hidden" style={{ border: "1px solid #d8d1c4" }}>
                <img src={u.img} alt={u.title} loading="lazy" decoding="async" className="w-full object-cover" style={{ height: "200px" }} />
                <div className="p-6" style={{ backgroundColor: "white" }}>
                  <h3 className="font-display text-2xl mb-3" style={{ color: "#3a2b24" }}>{u.title}</h3>
                  <p className="text-sm leading-relaxed" style={{ color: "#3a2b24", opacity: 0.7, fontFamily: "'Satoshi', sans-serif" }}>{u.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Navigation links */}
      <section style={{ backgroundColor: "#2d5016" }} className="py-12 px-4 text-center">
        <p className="font-display text-2xl mb-6" style={{ color: "#f5f2ec" }}>En savoir plus</p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Link to="/la-paille" className="btn-secondary px-6 py-3 rounded-md font-body font-500 text-sm inline-flex items-center gap-2" data-testid="link-la-paille">
            La paille de Miscanthus <ArrowRight size={16} />
          </Link>
          <Link to="/les-rhizomes" className="inline-flex items-center gap-2 px-6 py-3 rounded-md text-sm font-body font-500" style={{ backgroundColor: "rgba(245,242,236,0.15)", color: "#f5f2ec", border: "1px solid rgba(245,242,236,0.3)" }} data-testid="link-rhizomes">
            Les rhizomes & plantation <ArrowRight size={16} />
          </Link>
        </div>
      </section>
    </main>
  );
}
